// WHY — one tile in the 8-tile metric grid. Click to expand inline to a big chart.
// Direction-aware delta coloring: red ▲ for "up-is-bad" metrics like CPL/Spend/CPM,
// green ▲ for "up-is-good" metrics like Leads/CTR/Conv Rate/Impressions/Clicks.
// Expanded state is owned by the parent so only one tile can be open at a time
// and the parent can grow the expanded tile to span the full grid row for a
// bigger chart canvas.

import { Sparkline } from "./Sparkline";

export type MetricDirection = "up-is-bad" | "up-is-good";

interface Props {
  label: string;
  value: string;                    // already formatted by parent
  deltaPct: number | null;
  sparkline: Array<number | null>;
  direction: MetricDirection;
  alertActive: boolean;
  expanded: boolean;
  onToggle: () => void;
  gridColumn?: string;              // e.g. "1 / -1" when expanded to span the row
  expandedChart?: React.ReactNode;  // optional big-chart node
}

type Tone = "up" | "down" | "flat";

interface DeltaResult {
  text: string;
  tone: Tone;
}

function formatDelta(deltaPct: number | null, direction: MetricDirection): DeltaResult {
  if (deltaPct === null || Math.abs(deltaPct) < 0.02) {
    return { text: "— flat", tone: "flat" };
  }
  const arrow = deltaPct > 0 ? "▲" : "▼";
  const absText = Math.abs(deltaPct).toFixed(1) + "%";
  const text = `${arrow} ${absText}`;
  // positive delta = up tone; negative = down tone
  // then map tone to color based on direction
  const rawTone: Tone = deltaPct > 0 ? "up" : "down";
  // for "up-is-bad": up is bad (red), down is good (green)
  // for "up-is-good": up is good (green), down is bad (red)
  // tone here drives color: "up" = red when up-is-bad, green when up-is-good
  return { text, tone: rawTone };
}

function toneColor(tone: Tone, direction: MetricDirection): string {
  if (tone === "flat") return "var(--color-bone-dim)";
  if (tone === "up") {
    return direction === "up-is-bad" ? "#d9645c" : "#6dbf78";
  }
  // down
  return direction === "up-is-bad" ? "#6dbf78" : "#d9645c";
}

export function MetricTile({
  label,
  value,
  deltaPct,
  sparkline,
  direction,
  alertActive,
  expanded,
  onToggle,
  gridColumn,
  expandedChart,
}: Props) {
  const { text: deltaText, tone } = formatDelta(deltaPct, direction);
  const deltaColor = toneColor(tone, direction);

  const tileBg = alertActive ? "rgba(217,100,92,0.08)" : "rgba(255,255,255,0.03)";

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      style={{
        background: tileBg,
        borderRadius: 4,
        padding: 12,
        minHeight: 44,
        border: "none",
        cursor: "pointer",
        color: "inherit",
        textAlign: "left",
        display: "block",
        width: "100%",
        gridColumn,
      }}
    >
      {/* Label */}
      <div
        style={{
          fontSize: "var(--fs-xs)",
          letterSpacing: "var(--letter-label)",
          textTransform: "uppercase",
          color: "var(--color-bone-dim)",
          fontFamily: "var(--font-mono)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>

      {/* Big value */}
      <div
        style={{
          fontSize: 24,
          fontFamily: "var(--font-mono)",
          fontWeight: 300,
        }}
      >
        {value}
      </div>

      {/* Delta line */}
      <div
        style={{
          fontSize: "var(--fs-xs)",
          color: deltaColor,
          marginTop: 2,
        }}
      >
        {deltaText}
      </div>

      {/* Sparkline (always visible) */}
      <div style={{ marginTop: 8 }}>
        <Sparkline points={sparkline} width={160} height={32} />
      </div>

      {/* Expanded chart */}
      {expanded && expandedChart && (
        <div style={{ marginTop: 12 }}>{expandedChart}</div>
      )}
    </button>
  );
}
